import { rulesEngineGet, rulesEngineRequest } from './rulesEngineClient';
import { getRulesEngineApiBaseUrl, getRulesEngineComplianceUrl } from './rulesEngineEndpoints';
import { getRuleSkipReasonLabel } from './complianceTranslations';

const getRulesBaseUrl = () => process.env.REACT_APP_API_RULES_URL;

const getResponseRoot = (response) => response?.data ?? response;

const extractCollection = (response, keys) => {
  const root = getResponseRoot(response);

  if (Array.isArray(root)) {
    return root;
  }

  for (const key of keys) {
    const candidate = root?.[key];

    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (candidate && Array.isArray(candidate.items)) {
      return candidate.items;
    }
  }

  return [];
};

const asDisplayText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

const resolvePolicyName = (policy, index) => {
  const directName = [
    policy?.name,
    policy?.title,
    policy?.plan_name,
    policy?.policy_name,
  ]
    .map(asDisplayText)
    .find(Boolean);

  if (directName) {
    return directName;
  }

  const planName = [
    policy?.plan?.code,
    policy?.plan?.name,
    policy?.plan?.id,
  ]
    .map(asDisplayText)
    .find(Boolean);

  return planName || `Política ${index + 1}`;
};

const resolvePolicyVersion = (policy) => {
  const version = [
    policy?.version,
    policy?.policy_version,
    policy?.revision,
    policy?.policy_revision,
    policy?.plan?.version,
  ]
    .map(asDisplayText)
    .find(Boolean);

  return version || '-';
};

const resolvePolicyCoverage = (policy) => {
  const coverage = [
    policy?.coverage,
    policy?.coverage_description,
    policy?.scope,
    policy?.scope_description,
  ]
    .map(asDisplayText)
    .find(Boolean);

  return coverage || '-';
};

const normalizePolicy = (policy, index = 0) => ({
  id: policy?.id || policy?._id || `policy-${index}`,
  clientId: asDisplayText(policy?.client_id || policy?.clientId) || null,
  organizationId: asDisplayText(policy?.organization_id || policy?.organizationId) || null,
  scopeType: asDisplayText(policy?.scope_type || policy?.scopeType) || 'client',
  transactionTypeKey: asDisplayText(policy?.transaction_type_key || policy?.transactionTypeKey) || 'all',
  isDefault: Boolean(policy?.is_default ?? policy?.isDefault),
  priority: asDisplayText(policy?.priority) || '',
  name: asDisplayText(policy?.name) || resolvePolicyName(policy, index),
  planId: asDisplayText(policy?.plan?.id || policy?.plan_id) || null,
  version: resolvePolicyVersion(policy),
  coverage: resolvePolicyCoverage(policy),
  status: policy?.status || policy?.state || policy?.is_active || policy?.active || 'unknown',
  latestChange: policy?.latest_change || null,
  raw: policy,
});

const DATA_BLOCK_SKIP_REASONS = ['blocked_by_data', 'blocked_due_to_data', 'bloqueada_por_dados'];

const normalizeSkipReasonCode = (value) => asDisplayText(value).toLowerCase();

const isDataInsufficientSkipReason = (reason) => {
  const normalizedReason = normalizeSkipReasonCode(reason);
  if (!normalizedReason) return false;

  const [code] = normalizedReason.split(':');
  return DATA_BLOCK_SKIP_REASONS.includes(asDisplayText(code));
};

const isDataBlockedRule = (rule) => {
  const candidates = [
    rule?.effective_skip_reason,
    rule?.skip_reason,
    rule?.blocked_reason,
    rule?.lock_reason,
    rule?.restriction_reason,
    rule?.implementation_mode,
    rule?.rule_catalog_version?.implementation_mode,
  ];

  return candidates.some((candidate) => isDataInsufficientSkipReason(candidate));
};

const resolveBlockedReason = (rule) => {
  const directReason = rule?.blocked_reason || rule?.lock_reason || rule?.restriction_reason || '';
  if (directReason) {
    if (isDataInsufficientSkipReason(directReason)) {
      return getRuleSkipReasonLabel(normalizeSkipReasonCode(directReason));
    }

    return getRuleSkipReasonLabel(directReason);
  }

  const effectiveSkipReason = rule?.effective_skip_reason || rule?.skip_reason || '';
  if (!isDataInsufficientSkipReason(effectiveSkipReason)) return '';

  return getRuleSkipReasonLabel(normalizeSkipReasonCode(effectiveSkipReason));
};

const inferCoafGroupFromRuleCode = (ruleCode) => {
  const normalizedCode = asDisplayText(ruleCode).toUpperCase();
  if (!normalizedCode) {
    return null;
  }

  const [groupPrefix] = normalizedCode.split('-');
  return groupPrefix || null;
};

const normalizeRule = (rule, index = 0) => {
  const blockedReason = resolveBlockedReason(rule);
  const isBlockedByData = isDataBlockedRule(rule);

  const canDisable = Boolean(
    rule?.can_disable_rule ?? rule?.changeable ?? rule?.editable ?? rule?.can_override ?? rule?.can_client_disable,
  );
  const planRuleId = asDisplayText(rule?.plan_rule_id || rule?.id || rule?._id) || `rule-${index}`;
  const normalizedCode = rule?.code || rule?.rule_code || rule?.key || rule?.rule_id || `RULE-${index + 1}`;
  const activeFromPayload = Boolean(rule?.effective_enabled ?? rule?.active ?? rule?.enabled ?? rule?.is_active ?? rule?.enabled_by_default);

  return ({
  id: planRuleId,
  planRuleId,
  planId: asDisplayText(rule?.plan_id || rule?.planId) || null,
  code: normalizedCode,
  title: rule?.title || rule?.name || rule?.description || rule?.rule_name || 'Regra sem título',
  coafGroup: asDisplayText(rule?.coaf_group || rule?.coafGroup) || inferCoafGroupFromRuleCode(normalizedCode),
  coafItem: asDisplayText(rule?.coaf_item || rule?.coafItem) || null,
  active: isBlockedByData ? false : activeFromPayload,
  mandatoryLevel: asDisplayText(rule?.mandatory_level || rule?.mandatoryLevel) || null,
  changeable: isBlockedByData ? false : canDisable,
  canDisableRule: isBlockedByData ? false : canDisable,
  canEditThresholds: isBlockedByData ? false : Boolean(rule?.can_edit_thresholds ?? (Array.isArray(rule?.editable_threshold_keys) && rule.editable_threshold_keys.length > 0)),
  editableThresholdKeys: Array.isArray(rule?.editable_threshold_keys)
    ? rule.editable_threshold_keys.map((key) => asDisplayText(key)).filter(Boolean)
    : [],
  lastUpdatedAt: asDisplayText(rule?.last_updated_at || rule?.lastUpdatedAt),
  lastUpdatedBy: asDisplayText(rule?.last_updated_by || rule?.lastUpdatedBy),
  blockedReason,
  raw: rule,
});
};

const getRulesEngineAdminPlanRulesUrl = (rawBaseUrl, planId) => {
  if (!planId) {
    throw new Error('planId is required to manage plan rules');
  }

  return `${getRulesEngineApiBaseUrl(rawBaseUrl)}/admin/plans/${planId}/plan_rules`;
};

const normalizeOverride = (override, index = 0) => ({
  id: override?.id || override?._id || override?.override_id || override?.overrideId || `override-${index}`,
  ruleId: override?.rule_id || override?.ruleId || override?.rule?.id || override?.rule?._id || null,
  ruleCode: asDisplayText(override?.rule_code || override?.ruleCode || override?.rule?.code || override?.rule?.rule_code),
  raw: override,
});

const normalizePolicyStatus = (status) => {
  if (typeof status === 'boolean') {
    return status ? 'active' : 'inactive';
  }

  if (typeof status === 'number') {
    return status === 1 ? 'active' : String(status);
  }

  return asDisplayText(status).toLowerCase();
};

const isActivePolicy = (policy) => {
  const normalizedStatus = normalizePolicyStatus(policy?.status);

  return ['active', 'enabled', 'current', 'true', '1'].includes(normalizedStatus);
};

const normalizeRuleCode = (value) => asDisplayText(value).toUpperCase();

const buildOverridesLookup = (overrides = []) => {
  const byRuleId = new Map();
  const byRuleCode = new Map();

  overrides.forEach((override) => {
    if (override.ruleId) {
      byRuleId.set(String(override.ruleId), override.id);
    }

    const normalizedCode = normalizeRuleCode(override.ruleCode);

    if (normalizedCode) {
      byRuleCode.set(normalizedCode, override.id);
    }
  });

  return { byRuleId, byRuleCode };
};

const getOverrideId = (rule) => rule?.raw?.override_id || rule?.raw?.overrideId || rule?.raw?.existing_override_id || rule?.raw?.override?.id || null;

const resolveOverrideIdForChange = (change, overridesLookup, options = {}) => {
  const skipDirectOverrideId = Boolean(options?.skipDirectOverrideId);
  const directOverrideId = skipDirectOverrideId ? null : getOverrideId(change?.rule);

  if (directOverrideId) {
    return directOverrideId;
  }

  if (change?.id) {
    const overrideByRuleId = overridesLookup.byRuleId.get(String(change.id));

    if (overrideByRuleId) {
      return overrideByRuleId;
    }
  }

  const normalizedCode = normalizeRuleCode(change?.code);

  if (normalizedCode) {
    return overridesLookup.byRuleCode.get(normalizedCode) || null;
  }

  return null;
};

const extractOverrideErrorMessage = (error) => {
  const direct = asDisplayText(error?.response?.data?.error || error?.response?.data?.message);
  if (direct) {
    return direct.toLowerCase();
  }

  const backend = asDisplayText(error?.backendMessages || error?.cause?.response?.data?.error || error?.cause?.response?.data?.message);
  if (backend) {
    return backend.toLowerCase();
  }

  const fallback = asDisplayText(error?.message);
  return fallback.toLowerCase();
};

const isOverrideNotFoundError = (error) => {
  const status = Number(error?.status || error?.response?.status || error?.cause?.response?.status || 0);
  if (status !== 404) {
    return false;
  }

  const message = extractOverrideErrorMessage(error);
  return message.includes('override not found');
};

const isRuleCatalogAlreadyTakenError = (error) => {
  const status = Number(error?.status || error?.response?.status || error?.cause?.response?.status || 0);
  if (status !== 422) {
    return false;
  }

  const message = extractOverrideErrorMessage(error)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return message.includes('rule catalog') && (message.includes('already') || message.includes('ja esta em uso'));
};

const buildOverridePayload = (policyId, change) => ({
  policy_id: policyId,
  ...(change.id ? { rule_id: change.id } : {}),
  ...(change.code ? { rule_code: change.code } : {}),
  active: change.nextActive,
  enabled: change.nextActive,
  reason: asDisplayText(change.reason) || (change.nextActive ? 'Reativacao via tela de regras' : 'Desativacao via tela de regras'),
  approved_by: 'self_service_ui',
  changeable: Boolean(change.changeable),
  editable: Boolean(change.changeable),
});

export const getClientPolicies = async (options = {}) => {
  const params = new URLSearchParams();

  const clientId = asDisplayText(options?.clientId);
  if (clientId) {
    params.set('client_id', clientId);
  }

  const organizationId = asDisplayText(options?.organizationId);
  if (organizationId) {
    params.set('organization_id', organizationId);
  }

  const scopeType = asDisplayText(options?.scopeType);
  if (scopeType) {
    params.set('scope_type', scopeType);
  }

  const transactionTypeKey = asDisplayText(options?.transactionTypeKey);
  if (transactionTypeKey) {
    params.set('transaction_type_key', transactionTypeKey);
  }

  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await rulesEngineGet(getRulesEngineComplianceUrl(getRulesBaseUrl(), `client_policies${query}`));
  const policies = extractCollection(response, ['items', 'policies', 'data', 'results']);

  return policies.map((policy, index) => normalizePolicy(policy, index));
};

export const getAdminPlans = async () => {
  const response = await rulesEngineGet(`${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/plans`);
  const plans = extractCollection(response, ['items', 'plans', 'data', 'results']);

  return plans.map((plan, index) => ({
    id: asDisplayText(plan?.id || plan?._id) || `plan-${index}`,
    code: asDisplayText(plan?.code) || '-',
    name: asDisplayText(plan?.name) || `Plano ${index + 1}`,
    version: asDisplayText(plan?.version) || '-',
    status: asDisplayText(plan?.status) || 'unknown',
    targetSegment: asDisplayText(plan?.target_segment),
    effectiveFrom: asDisplayText(plan?.effective_from),
    effectiveTo: asDisplayText(plan?.effective_to),
    latestChange: plan?.latest_change || null,
    ruleCount: Number(plan?.rule_count) || 0,
    coafGroups: Array.isArray(plan?.coaf_groups) ? plan.coaf_groups : [],
    raw: plan,
  }));
};

export const getAdminRuleCatalogs = async () => {
  const response = await rulesEngineGet(`${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/rule_catalogs`);
  const catalogs = extractCollection(response, ['items', 'rule_catalogs', 'data', 'results']);

  return catalogs.map((catalog, index) => ({
    id: asDisplayText(catalog?.id || catalog?._id) || `catalog-${index}`,
    ruleCode: asDisplayText(catalog?.rule_code) || '-',
    coafGroup: asDisplayText(catalog?.coaf_group) || '-',
    coafItem: asDisplayText(catalog?.coaf_item) || '-',
    title: asDisplayText(catalog?.title) || '-',
    status: asDisplayText(catalog?.status) || 'unknown',
    raw: catalog,
  }));
};

const normalizeOrganization = (organization, index = 0) => ({
  id: asDisplayText(organization?.id || organization?._id) || `org-${index}`,
  name: asDisplayText(organization?.name) || `Organizacao ${index + 1}`,
  status: asDisplayText(organization?.status) || 'unknown',
  createdAt: asDisplayText(organization?.created_at),
  updatedAt: asDisplayText(organization?.updated_at),
  latestChange: organization?.latest_change || organization?.latestChange || null,
  raw: organization,
});

const normalizeAdminClient = (client, index = 0) => ({
  id: asDisplayText(client?.id || client?._id) || `client-${index}`,
  clientId: asDisplayText(client?.client_id) || `client-${index + 1}`,
  organizationId: asDisplayText(client?.organization_id),
  name: asDisplayText(client?.name),
  status: asDisplayText(client?.status) || 'unknown',
  planId: client?.plan_id || null,
  createdAt: asDisplayText(client?.created_at),
  updatedAt: asDisplayText(client?.updated_at),
  raw: client,
});

const buildAdminOrganizationPayload = (payload = {}) => {
  const normalized = {
    ...(payload.id ? { id: payload.id } : {}),
    ...(payload.name ? { name: payload.name } : {}),
    ...(payload.status ? { status: payload.status } : {}),
    ...(payload.reason ? { reason: payload.reason } : {}),
  };

  return normalized;
};

const buildAdminClientPayload = (payload = {}) => {
  const normalized = {
    ...(payload.clientId ? { client_id: payload.clientId } : {}),
    ...(payload.organizationId ? { organization_id: payload.organizationId } : {}),
    ...(payload.name ? { name: payload.name } : {}),
    ...(payload.status ? { status: payload.status } : {}),
    // So relevante na criacao -- se ausente, a fonte de dados nao ganha
    // nenhuma ClientPolicy propria e passa a herdar a politica da
    // organizacao (ver Compliance::Client, sem callback de policy default).
    ...(payload.planId ? { plan_id: payload.planId } : {}),
    ...(payload.reason ? { reason: payload.reason } : {}),
  };

  return normalized;
};

export const getAdminOrganizations = async () => {
  const response = await rulesEngineGet(`${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/organizations`);
  const organizations = extractCollection(response, ['items', 'organizations', 'data', 'results']);

  return organizations.map((organization, index) => normalizeOrganization(organization, index));
};

export const createAdminOrganization = async (payload = {}) => {
  const response = await rulesEngineRequest({
    method: 'post',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/organizations`,
    data: buildAdminOrganizationPayload(payload),
  });

  return normalizeOrganization(response?.data ?? response);
};

export const updateAdminOrganization = async (organizationId, payload = {}) => {
  if (!organizationId) {
    throw new Error('organizationId is required to update organization');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/organizations/${organizationId}`,
    data: buildAdminOrganizationPayload(payload),
  });

  return normalizeOrganization(response?.data ?? response);
};

export const deactivateAdminOrganization = async (organizationId, reason = '') => {
  if (!organizationId) {
    throw new Error('organizationId is required to deactivate organization');
  }

  const response = await rulesEngineRequest({
    method: 'post',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/organizations/${organizationId}/deactivate`,
    data: reason ? { reason } : {},
  });

  return normalizeOrganization(response?.data ?? response);
};

export const getAdminClients = async () => {
  const response = await rulesEngineGet(`${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/clients`);
  const clients = extractCollection(response, ['items', 'clients', 'data', 'results']);

  return clients.map((client, index) => normalizeAdminClient(client, index));
};

export const createAdminClient = async (payload = {}) => {
  const response = await rulesEngineRequest({
    method: 'post',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/clients`,
    data: buildAdminClientPayload(payload),
  });

  return normalizeAdminClient(response?.data ?? response);
};

export const updateAdminClient = async (clientRecordId, payload = {}) => {
  if (!clientRecordId) {
    throw new Error('clientRecordId is required to update client');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/clients/${clientRecordId}`,
    data: buildAdminClientPayload(payload),
  });

  return normalizeAdminClient(response?.data ?? response);
};

const buildAdminPlanPayload = (payload = {}) => {
  const planPayload = {
    ...(payload.name ? { name: payload.name } : {}),
    ...(payload.code ? { code: payload.code } : {}),
    ...(payload.description ? { description: payload.description } : {}),
    ...(payload.targetSegment ? { target_segment: payload.targetSegment } : {}),
    ...(payload.version !== undefined && payload.version !== null && payload.version !== ''
      ? { version: payload.version }
      : {}),
    ...(payload.status ? { status: payload.status } : {}),
  };

  if (payload.effectiveFrom) {
    planPayload.effective_from = payload.effectiveFrom;
  }

  if (payload.effectiveTo) {
    planPayload.effective_to = payload.effectiveTo;
  }

  return planPayload;
};

const buildAdminPlanRulesPayload = (planRules = []) => {
  if (!Array.isArray(planRules) || !planRules.length) {
    return [];
  }

  return planRules.map((planRule) => ({
    rule_catalog_id: planRule.ruleCatalogId,
    enabled_by_default: planRule.enabledByDefault ?? true,
    can_client_disable: planRule.canClientDisable ?? false,
    dependency_policy: planRule.dependencyPolicy ?? 'strict',
  }));
};

export const createAdminPlan = async (payload = {}) => {
  const response = await rulesEngineRequest({
    method: 'post',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/plans`,
    data: {
      ...buildAdminPlanPayload(payload),
      plan_rules: buildAdminPlanRulesPayload(payload.planRules),
    },
  });

  return response?.data ?? response;
};

export const updateAdminPlan = async (planId, payload = {}) => {
  if (!planId) {
    throw new Error('planId is required to update admin plan');
  }

  const data = {
    ...buildAdminPlanPayload(payload),
    ...(payload.reason ? { reason: payload.reason } : {}),
  };

  const planRulesPayload = buildAdminPlanRulesPayload(payload.planRules);
  if (planRulesPayload.length) {
    data.plan_rules = planRulesPayload;
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/plans/${planId}`,
    data,
  });

  return response?.data ?? response;
};

export const createAdminPlanVersion = async (planId, payload = {}) => {
  if (!planId) {
    throw new Error('planId is required to create admin plan version');
  }

  const data = {
    ...(payload.reason ? { reason: payload.reason } : {}),
    ...(payload.name ? { name: payload.name } : {}),
    ...(payload.code ? { code: payload.code } : {}),
    ...(payload.targetSegment ? { target_segment: payload.targetSegment } : {}),
  };

  const response = await rulesEngineRequest({
    method: 'post',
    url: `${getRulesEngineApiBaseUrl(getRulesBaseUrl())}/admin/plans/${planId}/create_version`,
    data,
  });

  return response?.data ?? response;
};

const buildClientPolicyPayload = (payload = {}) => {
  const policyPayload = {
    ...(payload.name ? { name: payload.name } : {}),
    ...(payload.organizationId ? { organization_id: payload.organizationId } : {}),
    ...(payload.scopeType ? { scope_type: payload.scopeType } : {}),
    ...(payload.transactionTypeKey ? { transaction_type_key: payload.transactionTypeKey } : {}),
    ...(payload.isDefault !== undefined ? { is_default: Boolean(payload.isDefault) } : {}),
    ...(payload.priority !== undefined && payload.priority !== null && payload.priority !== ''
      ? { priority: Number(payload.priority) }
      : {}),
    ...(payload.clientId ? { client_id: payload.clientId } : {}),
    ...(payload.planId ? { plan_id: payload.planId } : {}),
    ...(payload.planCode ? { plan_code: payload.planCode } : {}),
    ...(payload.planVersion !== undefined && payload.planVersion !== null && payload.planVersion !== ''
      ? { plan_version: payload.planVersion }
      : {}),
    ...(payload.overrideMode ? { override_mode: payload.overrideMode } : {}),
    ...(payload.status ? { status: payload.status } : {}),
    ...(payload.reason ? { reason: payload.reason } : {}),
    ...(payload.approvalReason ? { approval_reason: payload.approvalReason } : {}),
    ...(payload.approvedBy ? { approved_by: payload.approvedBy } : {}),
  };

  if (payload.effectiveFrom) {
    policyPayload.effective_from = payload.effectiveFrom;
  }

  if (payload.effectiveTo) {
    policyPayload.effective_to = payload.effectiveTo;
  }

  return policyPayload;
};

export const createClientPolicy = async (payload = {}) => {
  const response = await rulesEngineRequest({
    method: 'post',
    url: getRulesEngineComplianceUrl(getRulesBaseUrl(), 'client_policies'),
    data: buildClientPolicyPayload(payload),
  });

  return response?.data ?? response;
};

export const updateClientPolicy = async (policyId, payload = {}) => {
  if (!policyId) {
    throw new Error('policyId is required to update client policy');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: getRulesEngineComplianceUrl(getRulesBaseUrl(), `client_policies/${policyId}`),
    data: buildClientPolicyPayload(payload),
  });

  return response?.data ?? response;
};

export const getClientPolicyRules = async (policyId, options = {}) => {
  const clientId = asDisplayText(options?.clientId);
  const organizationId = asDisplayText(options?.organizationId);
  const params = new URLSearchParams();
  if (clientId) {
    params.set('client_id', clientId);
  }
  if (organizationId) {
    params.set('organization_id', organizationId);
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await rulesEngineGet(getRulesEngineComplianceUrl(getRulesBaseUrl(), `client_policies/${policyId}/rules${query}`));
  const rules = extractCollection(response, ['items', 'rules', 'data', 'results']);

  return rules.map((rule, index) => normalizeRule(rule, index));
};

export const getAdminPlanRules = async (planId) => {
  if (!planId) {
    throw new Error('planId is required to load admin plan rules');
  }

  const response = await rulesEngineGet(getRulesEngineAdminPlanRulesUrl(getRulesBaseUrl(), planId));
  const rules = extractCollection(response, ['items', 'plan_rules', 'rules', 'data', 'results']);

  return rules.map((rule, index) => normalizeRule({ ...rule, plan_id: planId }, index));
};

const normalizeThresholdSnapshot = (thresholds = {}) => {
  return Object.entries(thresholds).reduce((acc, [key, rawEntry]) => {
    if (rawEntry && typeof rawEntry === 'object' && !Array.isArray(rawEntry)) {
      const value = rawEntry.value ?? rawEntry.effective_value ?? rawEntry.current_value ?? '';
      const thresholdValueId = rawEntry.threshold_value_id || rawEntry.id || rawEntry.value_id || null;

      acc.valuesByKey[key] = value;

      if (thresholdValueId) {
        acc.idsByKey[key] = thresholdValueId;
      }

      return acc;
    }

    acc.valuesByKey[key] = rawEntry;
    return acc;
  }, { valuesByKey: {}, idsByKey: {} });
};

const normalizeThresholdLimits = (thresholdLimits = {}) => {
  if (!thresholdLimits || typeof thresholdLimits !== 'object' || Array.isArray(thresholdLimits)) {
    return {};
  }

  return Object.entries(thresholdLimits).reduce((acc, [key, rawEntry]) => {
    if (!rawEntry || typeof rawEntry !== 'object' || Array.isArray(rawEntry)) {
      return acc;
    }

    const normalizedKey = asDisplayText(key);

    if (!normalizedKey) {
      return acc;
    }

    acc[normalizedKey] = {
      valueType: asDisplayText(rawEntry.value_type || rawEntry.valueType),
      min: rawEntry.min ?? null,
      max: rawEntry.max ?? null,
      step: rawEntry.step ?? null,
      defaultValue: rawEntry.default ?? rawEntry.default_value ?? null,
    };

    return acc;
  }, {});
};

export const getEffectiveThresholdsSnapshot = async (keys = [], planId = '', clientPolicyId = '', clientId = '') => {
  const normalizedKeys = Array.from(new Set((Array.isArray(keys) ? keys : [])
    .map((key) => asDisplayText(key))
    .filter(Boolean)));

  const response = await rulesEngineRequest({
    method: 'post',
    url: getRulesEngineComplianceUrl(getRulesBaseUrl(), 'thresholds/effective/query'),
    data: {
      ...(normalizedKeys.length ? { keys: normalizedKeys } : {}),
      ...(asDisplayText(planId) ? { plan_id: asDisplayText(planId) } : {}),
      ...(asDisplayText(clientPolicyId) ? { client_policy_id: asDisplayText(clientPolicyId) } : {}),
      ...(asDisplayText(clientId) ? { client_id: asDisplayText(clientId) } : {}),
    },
  });
  const root = getResponseRoot(response);
  const thresholds = root?.thresholds && typeof root.thresholds === 'object' ? root.thresholds : {};
  const effectiveThresholdProfileId = asDisplayText(root?.effective_threshold_profile_id);
  const thresholdLimitsByKey = normalizeThresholdLimits(root?.threshold_limits);

  return {
    ...normalizeThresholdSnapshot(thresholds),
    effectiveThresholdProfileId,
    thresholdLimitsByKey,
  };
};

export const getEffectiveThresholds = async (keys = []) => {
  const snapshot = await getEffectiveThresholdsSnapshot(keys);
  return snapshot.valuesByKey;
};

const getRulesEngineThresholdAdminUrl = (rawBaseUrl, thresholdProfileId) => {
  if (!thresholdProfileId) {
    throw new Error('thresholdProfileId is required to save threshold values');
  }

  return `${getRulesEngineApiBaseUrl(rawBaseUrl)}/admin/threshold_profiles/${thresholdProfileId}/threshold_values`;
};

const getRulesEnginePolicyThresholdUrl = (rawBaseUrl, policyId, key) => {
  if (!policyId) {
    throw new Error('policyId is required to save policy threshold values');
  }

  if (!key) {
    throw new Error('key is required to save policy threshold values');
  }

  return getRulesEngineComplianceUrl(rawBaseUrl, `client_policies/${policyId}/thresholds/by_key/${encodeURIComponent(key)}`);
};

const resolveThresholdValueIdForChange = (change, thresholdValueIdsByKey = {}) => {
  if (change?.thresholdValueId) {
    return change.thresholdValueId;
  }

  return thresholdValueIdsByKey[change?.key] || null;
};

const extractThresholdValueId = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return payload.threshold_value_id || payload.id || payload.value_id || null;
};

export const saveThresholdValues = async (thresholdProfileId, changes = [], thresholdValueIdsByKey = {}) => {
  if (!Array.isArray(changes) || !changes.length) {
    return { successful: [], failed: [], total: 0 };
  }

  const settled = await Promise.allSettled(changes.map(async (change) => {
    const thresholdValueId = resolveThresholdValueIdForChange(change, thresholdValueIdsByKey);
    const method = thresholdValueId ? 'patch' : 'put';
    const url = thresholdValueId
      ? `${getRulesEngineThresholdAdminUrl(getRulesBaseUrl(), thresholdProfileId)}/${thresholdValueId}`
      : `${getRulesEngineThresholdAdminUrl(getRulesBaseUrl(), thresholdProfileId)}/by_key/${encodeURIComponent(change.key)}`;

    const response = await rulesEngineRequest({
      method,
      url,
      data: {
        key: change.key,
        value: change.draftValue,
      },
    });

    const payload = response?.data ?? response;

    return {
      change,
      response: payload,
      thresholdValueId: thresholdValueId || extractThresholdValueId(payload),
      method,
    };
  }));

  const successful = [];
  const failed = [];

  settled.forEach((item, index) => {
    if (item.status === 'fulfilled') {
      successful.push(item.value);
      return;
    }

    failed.push({
      change: changes[index],
      error: item.reason,
    });
  });

  return { successful, failed, total: changes.length };
};

export const savePolicyThresholdValues = async (policyId, changes = [], reason = 'Ajuste de limiar via tela de regras', options = {}) => {
  if (!policyId) {
    throw new Error('policyId is required to save policy threshold values');
  }

  if (!Array.isArray(changes) || !changes.length) {
    return { successful: [], failed: [], total: 0 };
  }

  const settled = await Promise.allSettled(changes.map(async (change) => {
    const response = await rulesEngineRequest({
      method: 'put',
      url: getRulesEnginePolicyThresholdUrl(getRulesBaseUrl(), policyId, change.key),
      data: {
        value: change.draftValue,
        reason: asDisplayText(reason),
        ...(asDisplayText(options?.clientId) ? { client_id: asDisplayText(options.clientId) } : {}),
        ...(asDisplayText(options?.organizationId) ? { organization_id: asDisplayText(options.organizationId) } : {}),
      },
    });

    const payload = response?.data ?? response;

    return {
      change,
      response: payload,
      thresholdValueId: extractThresholdValueId(payload),
      method: 'put',
    };
  }));

  const successful = [];
  const failed = [];

  settled.forEach((item, index) => {
    if (item.status === 'fulfilled') {
      successful.push(item.value);
      return;
    }

    failed.push({
      change: changes[index],
      error: item.reason,
    });
  });

  return { successful, failed, total: changes.length };
};

export const getClientPolicyOverrides = async (policyId, options = {}) => {
  const clientId = asDisplayText(options?.clientId);
  const organizationId = asDisplayText(options?.organizationId);
  const params = new URLSearchParams();
  if (clientId) {
    params.set('client_id', clientId);
  }
  if (organizationId) {
    params.set('organization_id', organizationId);
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await rulesEngineGet(getRulesEngineComplianceUrl(getRulesBaseUrl(), `client_policies/${policyId}/overrides${query}`));
  const overrides = extractCollection(response, ['items', 'overrides', 'data', 'results']);

  return overrides.map((override, index) => normalizeOverride(override, index));
};

export const getActiveClientPolicySnapshot = async (options = {}) => {
  const policies = await getClientPolicies(options);
  const activePolicy = policies.find((policy) => isActivePolicy(policy)) || policies[0] || null;

  if (!activePolicy) {
    return { policy: null, rules: [] };
  }

  const rules = await getClientPolicyRules(activePolicy.id, options);

  return { policy: activePolicy, rules };
};

export const saveClientPolicyOverrides = async (policyId, changes = [], options = {}) => {
  if (!policyId) {
    throw new Error('policyId is required to save overrides');
  }

  if (!Array.isArray(changes) || !changes.length) {
    return { successful: [], failed: [], total: 0 };
  }

  const overrides = await getClientPolicyOverrides(policyId, options);
  const overridesLookup = buildOverridesLookup(overrides);

  const settled = await Promise.allSettled(changes.map(async (change) => {
    const overrideId = resolveOverrideIdForChange(change, overridesLookup);
    const payload = {
      ...buildOverridePayload(policyId, change),
      ...(asDisplayText(options?.clientId) ? { client_id: asDisplayText(options.clientId) } : {}),
      ...(asDisplayText(options?.organizationId) ? { organization_id: asDisplayText(options.organizationId) } : {}),
    };

    const resolveLatestOverrideId = async () => {
      const latestOverrides = await getClientPolicyOverrides(policyId, options);
      const latestLookup = buildOverridesLookup(latestOverrides);
      return resolveOverrideIdForChange(change, latestLookup, { skipDirectOverrideId: true });
    };

    const executeRequest = async (method, path, selectedOverrideId = null) => {
      const response = await rulesEngineRequest({
        method,
        url: getRulesEngineComplianceUrl(getRulesBaseUrl(), path),
        data: payload,
      });

      return {
        change,
        response: response?.data ?? response,
        method,
        overrideId: method === 'patch' ? selectedOverrideId : null,
      };
    };

    if (!overrideId) {
      try {
        return await executeRequest('post', `client_policies/${policyId}/overrides`);
      } catch (error) {
        if (!isRuleCatalogAlreadyTakenError(error)) {
          throw error;
        }

        const latestOverrideId = await resolveLatestOverrideId();
        if (!latestOverrideId) {
          throw error;
        }

        return executeRequest('patch', `client_policies/${policyId}/overrides/${latestOverrideId}`, latestOverrideId);
      }
    }

    try {
      return await executeRequest('patch', `client_policies/${policyId}/overrides/${overrideId}`, overrideId);
    } catch (error) {
      if (!isOverrideNotFoundError(error)) {
        throw error;
      }

      const latestOverrideId = await resolveLatestOverrideId();
      if (latestOverrideId) {
        return executeRequest('patch', `client_policies/${policyId}/overrides/${latestOverrideId}`, latestOverrideId);
      }

      try {
        return await executeRequest('post', `client_policies/${policyId}/overrides`);
      } catch (postError) {
        if (!isRuleCatalogAlreadyTakenError(postError)) {
          throw postError;
        }

        const recoveredOverrideId = await resolveLatestOverrideId();
        if (!recoveredOverrideId) {
          throw postError;
        }

        return executeRequest('patch', `client_policies/${policyId}/overrides/${recoveredOverrideId}`, recoveredOverrideId);
      }
    }
  }));

  const successful = [];
  const failed = [];

  settled.forEach((item, index) => {
    if (item.status === 'fulfilled') {
      successful.push(item.value);
      return;
    }

    failed.push({
      change: changes[index],
      error: item.reason,
    });
  });

  return { successful, failed, total: changes.length };
};

export const saveAdminPlanRuleStates = async (planId, changes = []) => {
  if (!planId) {
    throw new Error('planId is required to save plan rule states');
  }

  if (!Array.isArray(changes) || !changes.length) {
    return { successful: [], failed: [], total: 0 };
  }

  const settled = await Promise.allSettled(changes.map(async (change) => {
    const planRuleId = change?.planRuleId || change?.id;

    if (!planRuleId) {
      throw new Error('planRuleId is required to save plan rule state');
    }

    const response = await rulesEngineRequest({
      method: 'patch',
      url: `${getRulesEngineAdminPlanRulesUrl(getRulesBaseUrl(), planId)}/${planRuleId}`,
      data: {
        enabled_by_default: change.nextActive,
        reason: asDisplayText(change.reason) || (change.nextActive ? 'Reativacao administrativa via tela de regras' : 'Desativacao administrativa via tela de regras'),
      },
    });

    return {
      change,
      response: response?.data ?? response,
      method: 'patch',
    };
  }));

  const successful = [];
  const failed = [];

  settled.forEach((item, index) => {
    if (item.status === 'fulfilled') {
      successful.push(item.value);
      return;
    }

    failed.push({
      change: changes[index],
      error: item.reason,
    });
  });

  return { successful, failed, total: changes.length };
};

export const saveAdminPlanRuleGroupState = async (planId, coafGroup, enabledByDefault, reason) => {
  if (!planId) {
    throw new Error('planId is required to change group state');
  }

  if (!coafGroup) {
    throw new Error('coafGroup is required to change group state');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: `${getRulesEngineAdminPlanRulesUrl(getRulesBaseUrl(), planId)}/group_state`,
    data: {
      coaf_group: coafGroup,
      enabled_by_default: enabledByDefault,
      reason: asDisplayText(reason),
    },
  });

  return response?.data ?? response;
};

export const normalizeClientPolicy = normalizePolicy;
export const normalizeClientRule = normalizeRule;
